import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Bell, ChevronDown, ChevronRight, CircleHelp, LogOut, Menu, Monitor, Moon, Sun, User } from "lucide-react";
import { cn } from "../../lib/cn";
import { Avatar } from "../Avatar";
import { useTheme } from "../../hooks/useTheme";
import type { ThemePreference } from "../../hooks/useTheme";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface WorkspaceOption {
  id: string;
  name: string;
}

export interface UserMenuAction {
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Sai em `danger` (sair da conta, excluir). */
  destructive?: boolean;
  /** Ponto de "tem coisa nova" (ex.: Novidades). Aparece também no avatar. */
  badge?: boolean;
}

/** Par rótulo/valor pequeno de contexto (ex.: "Monitores · 3/3 ativos"). */
export interface TopbarStat {
  label: string;
  value: ReactNode;
}

export interface TopbarProps {
  /** Último item = página atual (não é link). */
  breadcrumbs: BreadcrumbItem[];
  /** Números de contexto à direita. Somem abaixo de 1280px — são luxo, não informação crítica. */
  stats?: TopbarStat[];
  /** Botão redondo de ajuda. Omitir esconde o botão. */
  onHelp?: () => void;
  /**
   * Sino de notificações. Omitir esconde o botão. `unread` acende o ponto vermelho
   * (o rótulo acessível diz que há novidade — o ponto não é o único sinal).
   */
  notifications?: { onClick: () => void; unread?: boolean };
  workspaces?: WorkspaceOption[];
  activeWorkspaceId?: string;
  onWorkspaceChange?: (id: string) => void;
  /** `role` aparece embaixo do nome no botão de conta (ex.: "Dono do workspace"). */
  user?: { name: string; email?: string; avatarUrl?: string | null; role?: string };
  userActions?: UserMenuAction[];
  /** Conteúdo do menu do usuário acima das ações (ex.: `DensityToggle`). */
  userMenuContent?: ReactNode;
  /** Filtro de período, busca, ações da página. */
  children?: ReactNode;
  /** Abre a sidebar como drawer. Injetado pelo AppShell. */
  onOpenMenu?: () => void;
  className?: string;
}

const THEME_LABEL: Record<ThemePreference, string> = {
  light: "Tema claro",
  dark: "Tema escuro",
  system: "Tema do sistema",
};

/**
 * Barra superior: PAINEL branco de 64px e raio 16 no desktop (flutua sobre o
 * canvas cinza, sem borda nem sombra); 48px encostada nas bordas no mobile.
 * Ações à direita são botões REDONDOS (ícone 16px num círculo de 36px).
 */
export function Topbar({
  breadcrumbs,
  stats,
  onHelp,
  notifications,
  workspaces,
  activeWorkspaceId,
  onWorkspaceChange,
  user,
  userActions,
  userMenuContent,
  children,
  onOpenMenu,
  className,
}: TopbarProps) {
  const { theme, cycleTheme } = useTheme();
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;

  return (
    <header
      className={cn(
        "sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3",
        "md:top-4 md:h-topbar md:gap-4 md:rounded-lg md:border-b-0 md:px-3.5",
        className,
      )}
    >
      {onOpenMenu ? (
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="Abrir menu de navegação"
          className="hit-44 -ml-1 inline-flex size-9 shrink-0 items-center justify-center rounded-pill text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
        >
          <Menu aria-hidden="true" className="size-5" />
        </button>
      ) : null}

      <Breadcrumbs items={breadcrumbs} />

      <div className="ml-auto flex shrink-0 items-center gap-1.5 md:gap-2">
        {stats && stats.length > 0 ? (
          <dl className="mr-2 hidden items-center gap-5 xl:flex">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1 whitespace-nowrap">
                <dt className="text-2xs font-normal text-subtle-foreground">{stat.label}</dt>
                <dd className="nums m-0 text-sm font-semibold leading-none">{stat.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {children}

        {workspaces && workspaces.length > 0 ? (
          <WorkspaceSelect
            workspaces={workspaces}
            {...(activeWorkspaceId !== undefined ? { activeWorkspaceId } : {})}
            {...(onWorkspaceChange ? { onWorkspaceChange } : {})}
          />
        ) : null}

        <button
          type="button"
          onClick={cycleTheme}
          aria-label={`${THEME_LABEL[theme]}. Trocar tema.`}
          title={THEME_LABEL[theme]}
          className={ROUND_BUTTON}
        >
          <ThemeIcon aria-hidden="true" className="size-4" />
        </button>

        {onHelp ? (
          <button type="button" onClick={onHelp} aria-label="Ajuda" title="Ajuda" className={cn(ROUND_BUTTON, "max-sm:hidden")}>
            <CircleHelp aria-hidden="true" className="size-4" />
          </button>
        ) : null}

        {notifications ? (
          <button
            type="button"
            onClick={notifications.onClick}
            aria-label={notifications.unread ? "Notificações (há novidades)" : "Notificações"}
            title="Notificações"
            className={ROUND_BUTTON}
          >
            <Bell aria-hidden="true" className="size-4" />
            {notifications.unread ? (
              <span
                aria-hidden="true"
                className="absolute right-2 top-2 size-1.5 rounded-full bg-danger ring-2 ring-surface"
              />
            ) : null}
          </button>
        ) : null}

        {user ? (
          <UserMenu user={user} actions={userActions ?? []} content={userMenuContent} />
        ) : null}
      </div>
    </header>
  );
}

/** Botão só-ícone redondo da topbar (contorno fino, 36px; alvo de 44px no toque). */
const ROUND_BUTTON = cn(
  "hit-44 relative inline-flex size-9 shrink-0 items-center justify-center rounded-pill border border-border bg-surface text-foreground",
  "transition-colors duration-[var(--duration-micro)] hover:bg-surface-subtle",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  const last = items.length - 1;

  return (
    <nav aria-label="Trilha de navegação" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-xs">
        {items.map((item, index) => {
          const isLast = index === last;
          return (
            <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {index > 0 ? (
                <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-subtle-foreground" />
              ) : null}
              {isLast ? (
                <span
                  aria-current="page"
                  className="truncate text-sm font-medium text-foreground"
                >
                  {item.label}
                </span>
              ) : item.href ? (
                <a
                  href={item.href}
                  className="hidden truncate rounded-sm text-subtle-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
                >
                  {item.label}
                </a>
              ) : (
                <span className="hidden truncate text-subtle-foreground sm:block">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface WorkspaceSelectProps {
  workspaces: WorkspaceOption[];
  activeWorkspaceId?: string;
  onWorkspaceChange?: (id: string) => void;
}

/**
 * `<select>` nativo de propósito: no mobile ele abre a roda do sistema,
 * que é alvo de toque perfeito e de graça. Não há busca aqui — quando a
 * lista de workspaces crescer, isso vira um combobox próprio.
 */
function WorkspaceSelect({
  workspaces,
  activeWorkspaceId,
  onWorkspaceChange,
}: WorkspaceSelectProps) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="sr-only">
        Workspace
      </label>
      <select
        id={id}
        value={activeWorkspaceId ?? ""}
        onChange={(event) => onWorkspaceChange?.(event.target.value)}
        className={cn(
          "h-9 max-w-[7rem] md:max-w-[9rem] truncate rounded-pill border border-transparent bg-muted px-3 text-xs font-medium text-foreground",
          "hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "max-md:min-h-11",
        )}
      >
        {workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </select>
    </>
  );
}

interface UserMenuProps {
  user: { name: string; email?: string; avatarUrl?: string | null; role?: string };
  actions: UserMenuAction[];
  content?: ReactNode;
}

function UserMenu({ user, actions, content }: UserMenuProps) {
  const hasNews = actions.some((action) => action.badge);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Conta de ${user.name}`}
        onClick={() => setOpen((value) => !value)}
        className="hit-44 inline-flex items-center gap-2.5 rounded-pill p-0.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:py-1 lg:pl-1 lg:pr-2"
      >
        <span className="relative inline-flex">
          <Avatar name={user.name} src={user.avatarUrl ?? null} size="md" />
          {hasNews ? (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-primary ring-2 ring-surface"
            />
          ) : null}
        </span>
        {/* nome + papel só onde cabe; no celular o avatar basta */}
        <span className="hidden min-w-0 flex-col lg:flex">
          <span className="max-w-[10rem] truncate text-sm font-semibold leading-tight">{user.name}</span>
          {user.role ? (
            <span className="max-w-[10rem] truncate text-2xs text-subtle-foreground">{user.role}</span>
          ) : null}
        </span>
        <ChevronDown aria-hidden="true" className="hidden size-3.5 shrink-0 text-subtle-foreground lg:block" />
      </button>

      <div
        id={menuId}
        role="menu"
        hidden={!open}
        aria-label="Conta"
        className="absolute right-0 top-full z-40 mt-2 w-60 rounded-lg border border-border bg-surface-raised p-1.5 shadow-md"
      >
        <div className="flex items-center gap-2 px-2 py-2">
          <User aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{user.name}</p>
            {user.email ? (
              <p className="truncate text-2xs text-muted-foreground">{user.email}</p>
            ) : null}
          </div>
        </div>

        {content ? (
          <>
            <div className="my-1 h-px bg-border" />
            <div className="px-2 py-1.5">{content}</div>
          </>
        ) : null}

        {actions.length > 0 ? <div className="my-1 h-px bg-border" /> : null}

        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              action.onSelect();
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm",
              "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "max-md:min-h-11",
              action.destructive ? "text-danger" : "text-foreground",
            )}
          >
            <span aria-hidden="true" className="shrink-0 [&_svg]:size-4 text-muted-foreground">
              {action.icon ?? <LogOut />}
            </span>
            <span className="flex-1">{action.label}</span>
            {action.badge ? (
              <>
                <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-primary" />
                <span className="sr-only">(novo)</span>
              </>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
